import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useOutletContext } from 'react-router-dom'
import { debounce } from 'lodash'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'

import SearchField from '../../FormLegacy/FormFieldsV2/SearchField'
import TextAreaField from '../../FormLegacy/FormFieldsV2/TextAreaField'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import PaginationFooter from '../../PaginationFooter/PaginationFooter'
import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import API from '../../../api/api'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import FeaturedTable from '../../FeaturedTable/FeaturedTable'
import VendorDetailsModal from '../modals/VisitorDetailsModal/VendorDetailsModal'
import ModalsContainer from '../modals/ModalsContainer'
import { readListEnvelope } from '../utils'
import { getWriteErrorMessage } from '../Guests/helpers'

import { getVendorColumns } from './helpers'

const Vendors = ({ selectedBuilding }) => {
  const { t } = useTranslation(['visitors', 'common'])
  const vendorStatuses = [
    { text: t('visitors:statuses.all'), value: 'all' },
    { text: t('visitors:statuses.active'), value: 'active' },
    { text: t('visitors:statuses.expired'), value: 'expired' },
    { text: t('visitors:statuses.banned'), value: 'banned' },
    { text: t('visitors:statuses.suspended'), value: 'suspended' },
  ]
  const typeLabel = t('visitors:snackbar.typeVendor')
  const { analyticsOpen, newVisitorModalOpen, setNewVisitorModalOpen } = useOutletContext()
  const [vendors, setVendors] = useState([])
  const [banVendorState, setBanVendorState] = useState(false)
  const [unbanVendorState, setUnbanVendorState] = useState(false)
  const [banReason, setBanReason] = useState('')
  const [isBanReasonError, setIsBanReasonError] = useState(false)
  const [checkInVendorState, setCheckInVendorState] = useState(false)
  const [search, setSearch] = useState('')
  const [isLoading, setLoading] = useState(false)
  const [isVendorsLoading, setIsVendorsLoading] = useState(false)
  const [showOnPage, setShowOnPage] = useState(25)
  const [pageNumber, setPageNumber] = useState(1)
  const [paginationItemsCount, setPaginationItemsCount] = useState(0)
  const [selectedTab, setSelectedTab] = useState(vendorStatuses[0])
  const [visitorDetailsId, setVisitorDetailsId] = useState(null)
  const { displaySnackbar } = useSnackbar()
  const [suspendVendorState, setSuspendVendorState] = useState(false)
  const [unsuspendVendorState, setUnsuspendVendorState] = useState(false)

  const onBanToggle = (vendor) => {
    if (vendor.banned) {
      setUnbanVendorState(vendor)
    } else {
      setBanVendorState(vendor)
      setBanReason('')
    }
  }

  const onCheckInClick = (vendor) => {
    setCheckInVendorState(vendor)
  }

  const onDetailsClick = (vendor) => {
    setVisitorDetailsId(vendor.id)
  }

  const onBanConfirm = async () => {
    if (!banReason) {
      setIsBanReasonError(true)
      return
    }

    setLoading(true)
    try {
      await API.updateVisitorStatus({
        id: banVendorState.id,
        type: 'vendor',
        banned: true,
        bannedReason: banReason,
      })
      displaySnackbar(t('visitors:snackbar.banned', { type: typeLabel, name: banVendorState.name }))
    } catch (err) {
      Sentry.captureException(err)
      displaySnackbar(
        getWriteErrorMessage(err, t('visitors:snackbar.errorBanning', { type: typeLabel })),
        true,
      )
    } finally {
      setBanVendorState(false)
      setBanReason('')
      setLoading(false)
      setIsBanReasonError(false)
      fetchVendors(search)
    }
  }

  const onUnbanConfirm = async () => {
    setLoading(true)
    try {
      await API.updateVisitorStatus({
        id: unbanVendorState.id,
        type: 'vendor',
        banned: false,
        bannedReason: '',
      })
      displaySnackbar(
        t('visitors:snackbar.unbanned', { type: typeLabel, name: unbanVendorState.name }),
      )
    } catch (err) {
      Sentry.captureException(err)
      displaySnackbar(
        getWriteErrorMessage(err, t('visitors:snackbar.errorUnbanning', { type: typeLabel })),
        true,
      )
    } finally {
      setUnbanVendorState(false)
      setLoading(false)
      fetchVendors(search)
    }
  }

  const onCheckInConfirm = async () => {
    setLoading(true)
    try {
      await API.checkInClientVendor(checkInVendorState.id)
      displaySnackbar(
        t('visitors:snackbar.checkedIn', { type: typeLabel, name: checkInVendorState.name }),
      )
    } catch (err) {
      Sentry.captureException(err)
      displaySnackbar(
        getWriteErrorMessage(err, t('visitors:snackbar.errorCheckingIn', { type: typeLabel })),
        true,
      )
    } finally {
      setCheckInVendorState(false)
      setLoading(false)
      fetchVendors(search)
    }
  }

  const onSuspendToggle = (vendor) => {
    if (vendor.suspended) {
      setUnsuspendVendorState(vendor)
    } else {
      setSuspendVendorState(vendor)
    }
  }

  const onSuspendConfirm = async () => {
    setLoading(true)
    try {
      await API.updateVisitorStatus({
        id: suspendVendorState.id,
        type: 'vendor',
        suspended: true,
      })
      displaySnackbar(
        t('visitors:snackbar.suspended', { type: typeLabel, name: suspendVendorState.name }),
      )
    } catch (err) {
      Sentry.captureException(err)
      displaySnackbar(
        getWriteErrorMessage(err, t('visitors:snackbar.errorSuspending', { type: typeLabel })),
        true,
      )
    } finally {
      setSuspendVendorState(false)
      setLoading(false)
      fetchVendors(search)
    }
  }

  const onUnsuspendConfirm = async () => {
    setLoading(true)
    try {
      await API.updateVisitorStatus({
        id: unsuspendVendorState.id,
        type: 'vendor',
        suspended: false,
      })
      displaySnackbar(
        t('visitors:snackbar.unsuspended', { type: typeLabel, name: unsuspendVendorState.name }),
      )
    } catch (err) {
      Sentry.captureException(err)
      displaySnackbar(
        getWriteErrorMessage(err, t('visitors:snackbar.errorUnsuspending', { type: typeLabel })),
        true,
      )
    } finally {
      setUnsuspendVendorState(false)
      setLoading(false)
      fetchVendors(search)
    }
  }

  const fetchVendors = async (searchTerm = search) => {
    if (!selectedBuilding) {
      return
    }
    setIsVendorsLoading(true)
    const skip = (pageNumber - 1) * showOnPage
    try {
      await API.getVendors({
        buildingId: selectedBuilding.id || '1',
        filter: selectedTab.value !== 'all' ? selectedTab.value : undefined,
        search: searchTerm,
        skip,
        limit: showOnPage,
      }).then((res) => {
        const { items, total } = readListEnvelope(res)
        setVendors(items)
        setPaginationItemsCount(total)
      })
    } catch (err) {
      Sentry.captureException(err)
      displaySnackbar(t('visitors:snackbar.errorFetchingVendors'), true)
    } finally {
      setIsVendorsLoading(false)
    }
  }

  const debouncedFetchVendors = debounce((searchTerm) => {
    fetchVendors(searchTerm)
  }, 500)

  const handleSearchChange = (e) => {
    const searchTerm = e.target.value
    setSearch(searchTerm)
    setPageNumber(1)
    debouncedFetchVendors(searchTerm)
  }

  useEffect(() => {
    fetchVendors()
  }, [pageNumber, showOnPage, selectedTab, selectedBuilding])

  const columns = getVendorColumns(
    t,
    onCheckInClick,
    onDetailsClick,
    onBanToggle,
    onSuspendToggle,
    selectedBuilding ? selectedBuilding.timezone : 'America/Chicago',
  )

  const maxTableHeight = {
    maxHeight: analyticsOpen ? `calc(100vh - 550px)` : `calc(100vh - 400px)`,
  }

  return (
    <div className="visitors">
      <div className="visitors__navbar">
        <div className="visitors__navbar-tabs">
          {vendorStatuses.map((tab) => (
            <div
              className={`visitors__navbar-tab ${tab.value === selectedTab.value ? 'active' : ''}`}
              key={tab.value}
              onClick={() => setSelectedTab(tab)}
            >
              {tab.text}
            </div>
          ))}
        </div>
        <SearchField
          placeholder={t('visitors:toolbar.searchByNameOrEmail')}
          value={search}
          onChange={handleSearchChange}
        />
      </div>
      <div
        className={`ctp-visitors__table ${isVendorsLoading || isLoading ? 'is-loading' : ''}`}
        style={maxTableHeight}
      >
        {vendors?.length > 0 ? (
          <FeaturedTable
            columns={columns}
            data={vendors}
            onRowClick={(row) => onDetailsClick(row.original)}
          />
        ) : (
          <div className="no-results">{t('visitors:views.noVendors')}</div>
        )}
      </div>
      {vendors?.length > 0 && (
        <PaginationFooter
          showOnPage={showOnPage}
          pageNumber={pageNumber}
          paginationItemsCount={paginationItemsCount}
          setSiteLoading={setIsVendorsLoading}
          setShowOnPage={setShowOnPage}
          setPageNumber={setPageNumber}
        />
      )}
      <ConfirmPopup
        title={t('visitors:confirm.confirmationTitle')}
        body={
          <div>
            <p>{t('visitors:confirm.banBody', { type: typeLabel, name: banVendorState.name })}</p>
            <div className="ban-reason-container">
              <TextAreaField
                name="ban-reason"
                label={t('visitors:columns.banReasonLabel')}
                placeholder={t('visitors:columns.banReasonPlaceholder', { type: typeLabel })}
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                isError={isBanReasonError}
              />
            </div>
          </div>
        }
        cancelText={t('common:actions.cancel')}
        confirmText={t('visitors:confirm.yesBan')}
        isOpen={!!banVendorState}
        cancelCallback={() => setBanVendorState(false)}
        confirmCallback={onBanConfirm}
        isLoading={isLoading}
      />
      <ConfirmPopup
        title={t('visitors:confirm.confirmationTitle')}
        body={
          unbanVendorState
            ? t('visitors:confirm.unbanBody', { type: typeLabel, name: unbanVendorState.name })
            : ''
        }
        cancelText={t('common:actions.cancel')}
        confirmText={t('visitors:confirm.yesUnban')}
        isOpen={!!unbanVendorState}
        cancelCallback={() => setUnbanVendorState(false)}
        confirmCallback={onUnbanConfirm}
        isLoading={isLoading}
      />
      <ConfirmPopup
        title={t('visitors:confirm.areYouSure')}
        className="is-positive"
        body={
          checkInVendorState
            ? t('visitors:confirm.checkInBody', { name: checkInVendorState.name })
            : ''
        }
        cancelText={t('visitors:confirm.no')}
        confirmText={t('visitors:confirm.yes')}
        isOpen={!!checkInVendorState}
        cancelCallback={() => setCheckInVendorState(false)}
        confirmCallback={onCheckInConfirm}
        isLoading={isLoading}
      />
      <ConfirmPopup
        title={t('visitors:confirm.confirmationTitle')}
        className="is-caution"
        body={
          suspendVendorState
            ? t('visitors:confirm.suspendBody', {
                type: typeLabel,
                name: suspendVendorState.name,
              })
            : ''
        }
        cancelText={t('common:actions.cancel')}
        confirmText={t('visitors:confirm.yesSuspend')}
        isOpen={!!suspendVendorState}
        cancelCallback={() => setSuspendVendorState(false)}
        confirmCallback={onSuspendConfirm}
        isLoading={isLoading}
      />
      <ConfirmPopup
        title={t('visitors:confirm.confirmationTitle')}
        className="is-caution"
        body={
          unsuspendVendorState
            ? t('visitors:confirm.unsuspendBody', {
                type: typeLabel,
                name: unsuspendVendorState.name,
              })
            : ''
        }
        cancelText={t('common:actions.cancel')}
        confirmText={t('visitors:confirm.yesUnsuspend')}
        isOpen={!!unsuspendVendorState}
        cancelCallback={() => setUnsuspendVendorState(false)}
        confirmCallback={onUnsuspendConfirm}
        isLoading={isLoading}
      />
      <VendorDetailsModal vendorId={visitorDetailsId} onClose={() => setVisitorDetailsId(null)} />
      <ModalsContainer
        newVisitorModalOpen={newVisitorModalOpen}
        setNewVisitorModalOpen={setNewVisitorModalOpen}
        onCreateSuccess={() => fetchVendors(search)}
      />
    </div>
  )
}

Vendors.propTypes = {
  selectedBuilding: PropTypes.object,
}

export default connect((state) => {
  return {
    selectedBuilding: selectActiveBuilding(state),
  }
})(Vendors)
